import PropTypes from 'prop-types';
import React from 'react';
import Button from '@mulesoft/anypoint-components/lib/Button';
import DropdownMenu from '@mulesoft/anypoint-components/lib/DropdownMenu';
import styles from './Pagination.css';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  testId = 'pagination'
}) => {
  const handlePageChange = React.useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        onPageChange(page);
      }
    },
    [totalPages, currentPage, onPageChange]
  );

  const handlePrevious = React.useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNext = React.useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const handleDropdownChange = React.useCallback(
    ({ value }) => {
      handlePageChange(value.key);
    },
    [handlePageChange]
  );

  const pageOptions = React.useMemo(() => {
    const options = [];

    for (let i = 1; i <= totalPages; i += 1) {
      options.push({
        key: i,
        label: i.toString()
      });
    }

    return options;
  }, [totalPages]);

  const selectedValue = React.useMemo(
    () => pageOptions.find((option) => option.key === currentPage),
    [pageOptions, currentPage]
  );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div id={testId} className={styles.pagination} data-test-id={testId}>
      <span data-test-id="left-content">
        <span id="page-info">
          <DropdownMenu
            testId="page-dropdown"
            options={pageOptions}
            value={selectedValue}
            onChange={handleDropdownChange}
            alignment="left"
          />{' '}
          of {totalPages} pages
        </span>
        <span id="items-info">({totalItems} items)</span>
      </span>
      <span className={styles.paginationControls} data-test-id="right-content">
        <Button
          kind="secondary"
          size="s"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={styles.navButton}
          data-test-id="previous-page"
        >
          Previous
        </Button>
        <Button
          kind="secondary"
          size="s"
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={styles.navButton}
          data-test-id="next-page"
        >
          Next
        </Button>
      </span>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  totalItems: PropTypes.number.isRequired,
  testId: PropTypes.string
};

export default Pagination;
